import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Calendar, MapPin, Phone, ClipboardList, CheckCircle2, PlayCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import Logo from "../components/Logo";

const STATUS_LABELS = {
    new: "Neu",
    confirmed: "Bestätigt",
    scheduled: "Geplant",
    in_progress: "Läuft",
    completed: "Erledigt",
    cancelled: "Storniert",
};
const SERVICE_LABELS = {
    privat_haushalt: "Wohnungsreinigung",
    buero: "Büro",
    gebaeude: "Gebäude",
    scheiben: "Scheiben & Schaufenster",
    begleitservice: "Begleitservice",
};
const STATUS_COLORS = {
    new: "bg-blue-50 text-blue-700",
    confirmed: "bg-violet-50 text-violet-700",
    scheduled: "bg-amber-50 text-amber-700",
    in_progress: "bg-orange-50 text-orange-700",
    completed: "bg-emerald-50 text-emerald-700",
    cancelled: "bg-slate-100 text-slate-600",
};

export default function EmployeeDashboard() {
    const { user, logout } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/bookings");
            setJobs(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/bookings/${id}`, { status });
            toast.success("Status aktualisiert");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC]" data-testid="employee-dashboard">
            <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="emp-logo-link">
                        <Logo variant="light" size="sm" />
                        <span className="text-[10px] uppercase tracking-wider text-[#16A34A] font-bold hidden sm:inline">Mitarbeiter Portal</span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <div className="text-right hidden sm:block">
                            <div className="text-sm font-semibold text-[#1A365D]">{user?.name}</div>
                            <div className="text-xs text-slate-500">{user?.email}</div>
                        </div>
                        <button onClick={logout} className="text-red-600 hover:text-red-700" data-testid="emp-logout-button" aria-label="Abmelden">
                            <LogOut className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-10">
                <div className="mb-8">
                    <h1 className="font-display font-black text-3xl text-[#1A365D] tracking-tight">Meine Aufträge</h1>
                    <p className="text-[#64748B] mt-1.5">Hier sehen Sie alle Ihnen zugewiesenen Einsätze.</p>
                </div>

                {loading ? (
                    <div className="text-center text-slate-500 py-12">Laden…</div>
                ) : jobs.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="emp-no-jobs">
                        <ClipboardList className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500">Aktuell sind Ihnen keine Aufträge zugewiesen.</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {jobs.map((j) => (
                            <div key={j.id} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`emp-job-${j.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_COLORS[j.status]}`}>{STATUS_LABELS[j.status]}</span>
                                            <span className="text-xs font-semibold text-[#16A34A] uppercase tracking-wider">{SERVICE_LABELS[j.service_type]}</span>
                                        </div>
                                        <h3 className="font-display font-bold text-xl text-[#1A365D]">{j.customer_name}</h3>
                                        <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm text-slate-600">
                                            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#16A34A]" /> {j.scheduled_date || j.preferred_date}{(j.scheduled_time || j.preferred_time) ? `, ${j.scheduled_time || j.preferred_time}` : ""}</div>
                                            <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#16A34A]" /> {j.customer_phone}</div>
                                            <div className="flex items-center gap-2 sm:col-span-2"><MapPin className="w-4 h-4 text-[#16A34A]" /> {j.address}, {j.postal_code} {j.city}</div>
                                        </div>
                                        {j.message ? <p className="mt-3 text-sm text-slate-600 italic">"{j.message}"</p> : null}
                                    </div>
                                    <div className="flex flex-col gap-2 min-w-[160px]">
                                        {j.status !== "in_progress" && j.status !== "completed" && (
                                            <button onClick={() => updateStatus(j.id, "in_progress")} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#1A365D] text-white text-xs font-semibold hover:bg-[#142A4A]" data-testid={`emp-start-${j.id}`}>
                                                <PlayCircle className="w-4 h-4" /> Starten
                                            </button>
                                        )}
                                        {j.status !== "completed" && (
                                            <button onClick={() => updateStatus(j.id, "completed")} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#16A34A] text-white text-xs font-semibold hover:bg-[#15803D]" data-testid={`emp-complete-${j.id}`}>
                                                <CheckCircle2 className="w-4 h-4" /> Abgeschlossen
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
